import { z } from "zod/v3";
import {
  TrackOccupation_Interaction,
  TrackOccupation as ProtoTrackOccupation,
  TrackOccupation_TrackGroupDrawDirection,
} from "@opstel/gen/knop/ui/v1/model/track_occupation_pb";
import { ZodOutputFor, createSchema } from "../zod-utils";
import { PlainMessage, Timestamp } from "@bufbuild/protobuf";
import { DateTime } from "luxon";
import { ShuntingUnit, shuntingUnitSchema } from "./shuntingUnit";
import { Action } from "@opstel/query/v2/models/action";
import { actionSchema } from "./action";
import { ElementOf } from "@shared/common/types/typeHelpers";

export interface TrackOccupation {
  startTime: DateTime;
  endTime: DateTime;
  order: number;
  actions: Action[];
  shuntingUnit: ShuntingUnit;
  arrivesAs: string;
  departsAs: string;
  originatesFrom: OccupationInteraction;
  dissipatesInto: OccupationInteraction;
  trackGroupDrawDirection: OccupationTrackGroupDrawDirection;
}
export interface ActiveOccupation extends TrackOccupation {
  activeAction: Action;
}
const OCCUPATION_INTERACTIONS = [
  "Unknown",
  "MoveSourceSide",
  "MoveTargetSide",
  "Split",
  "Combine",
  "PresentAtEdgeScenario",
  "Teleport",
] as const;
export type OccupationInteraction = ElementOf<typeof OCCUPATION_INTERACTIONS>;

const OCCUPATION_TRACK_GROUP_DRAW_DIRECTIONS = ["None", "Up", "Down", "Both"] as const;
export type OccupationTrackGroupDrawDirection = ElementOf<
  typeof OCCUPATION_TRACK_GROUP_DRAW_DIRECTIONS
>;

function transformInteractionEnum(interaction: TrackOccupation_Interaction): OccupationInteraction {
  return OCCUPATION_INTERACTIONS[interaction];
}

function transformTrackGroupDrawDirectionEnum(
  trackGroupDrawDirection: TrackOccupation_TrackGroupDrawDirection,
): OccupationTrackGroupDrawDirection {
  return OCCUPATION_TRACK_GROUP_DRAW_DIRECTIONS[trackGroupDrawDirection];
}

export function isPassingTrain(occupation: TrackOccupation) {
  return (
    occupation.actions.length === 1 &&
    ((occupation.originatesFrom === "MoveSourceSide" &&
      occupation.dissipatesInto === "MoveTargetSide") ||
      (occupation.originatesFrom === "MoveTargetSide" &&
        occupation.dissipatesInto === "MoveSourceSide"))
  );
}

export const trackOccupationSchema = createSchema<
  TrackOccupation,
  PlainMessage<ProtoTrackOccupation>
>({
  startTime: z
    .instanceof(Timestamp, { message: "TrackOccupation should have a start time" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  endTime: z
    .instanceof(Timestamp, { message: "TrackOccupation should have a end time" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  order: z.number(),
  actions: z.array(actionSchema).min(1, {
    message: "TrackOccupation should have actions (KnoP should filter occupations without actions)",
  }),
  shuntingUnit: shuntingUnitSchema,
  arrivesAs: z.string(),
  departsAs: z.string(),
  originatesFrom: z.nativeEnum(TrackOccupation_Interaction).transform(transformInteractionEnum),
  dissipatesInto: z.nativeEnum(TrackOccupation_Interaction).transform(transformInteractionEnum),
  trackGroupDrawDirection: z
    .nativeEnum(TrackOccupation_TrackGroupDrawDirection)
    .transform(transformTrackGroupDrawDirectionEnum),
}).refine((occupation) => occupation.startTime <= occupation.endTime, {
  message: "TrackOccupation start time should not be after end time",
}) satisfies ZodOutputFor<TrackOccupation>;

export const TrackOccupation = {
  trainUnitsInDrivingDirectionAtEnd: (trackOccupation: TrackOccupation) => {
    const units = trackOccupation.shuntingUnit.planTrainUnits.fromSourceToTarget(
      trackOccupation.actions.at(-1)!.orientation,
    );
    return trackOccupation.dissipatesInto === "MoveTargetSide" ? units.toReversed() : units;
  },
  trainUnitsInDrivingDirectionAtBegin: (trackOccupation: TrackOccupation) => {
    const units = trackOccupation.shuntingUnit.planTrainUnits.fromSourceToTarget(
      trackOccupation.actions.at(0)!.orientation,
    );
    return trackOccupation.originatesFrom === "MoveSourceSide" ? units.toReversed() : units;
  },
  getActiveOccupations: (occupations: TrackOccupation[], time: DateTime): ActiveOccupation[] => {
    const activeOccupations: ActiveOccupation[] = [];
    for (const occupation of occupations) {
      if (occupation.startTime <= time && time <= occupation.endTime) {
        const activeAction = occupation.actions.find((action) => Action.isActiveAt(action, time));
        if (activeAction !== undefined) {
          activeOccupations.push({ ...occupation, activeAction });
        }
      }
    }
    return activeOccupations;
  },
};
